import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { provideNucleusUiBase, CAPABILITY_CHECKER } from '@nucleus/ui-base';
import { provideConnectorClient, CONNECTOR_NOTIFIER } from '@nucleus/connector-admin-ui';
import { TasksenseConnectorNotifierAdapter } from './administration/connectors/connectors.adapter';
import { PermissionsService } from './services/permission/permissions.service';
import { DefaultFileViewer } from '@nucleus/file-viewer-ui';
import { ApplicationConfigService } from './services/application-config.service';
import { PermissionCheckerService } from './services/permission/permission-checker.service';
import { UIConfigurationService } from './services/ui-configuration.service';
import { NUCLEUS_THEME_STORAGE_KEY } from '@nucleus/ui-common';
import { CommonModule } from '@angular/common';
import { AppComponent } from './app.component';
import { Router, RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HeaderComponent } from './header/header.component';
import { HomeModule } from './home/home.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { appInitializer } from './initialize.function';
import { AuthenticationService } from './services/authentication.service';
import { ToastrModule } from 'ngx-toastr';
import { AppInterceptorService } from './services/app-interceptor.service';
import { OverlayService } from '@nucleus/ui-common';
import { GlassPanelComponent } from './glass-panel/glass-panel.component';
import { CookieService } from 'ngx-cookie-service';
import { FormsModule } from '@angular/forms';
import { InitService } from './services/init/init.service';
import { UserService } from './services/user/user.service';
import { AuditService } from './services/audit/audit.service';
import { LoadingService } from './services/init/loading-service';
import { FilterAutocompleteComponent } from "@nucleus/ui-base";
import { NgIdleModule } from '@ng-idle/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SettingsModule } from './settings/settings.module';
import { SchedulingModule } from './scheduling/scheduling.module';
import { ReportsModule } from './reports/reports.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { AdministrationModule } from './administration/administration.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { GANTT_GLOBAL_CONFIG, GanttI18nLocale, NgxGanttModule } from '@worktile/gantt';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { provideUserClient } from '@nucleus/user-ui';
import { provideAuditClient } from '@nucleus/audit-report-ui';
import { provideMailClient } from '@nucleus/mail-report-ui';
import { SchedulerApiModule, BASE_PATH as SchedulerBASE_PATH } from './api/scheduler-service';
import { UserDataApiModule, BASE_PATH as UserDataBASE_PATH } from './api/user-data-service';
import { AuthenticationApiModule, BASE_PATH as AuthenticationBASE_PATH } from './api/authentication-service';
import { RunnerApiModule, BASE_PATH as RunnerBASE_PATH } from './api/runner-service';
import { CapabilityApiModule, BASE_PATH as CapabilityBASE_PATH } from './api/capability-service';
import { provideReferenceDataClient } from '@nucleus/reference-data-ui';
import { provideOrgUnitClient } from '@nucleus/org-unit-ui';
import { provideSystemPropertyClient } from '@nucleus/system-property-ui';
import { importProvidersFrom } from '@angular/core';

@NgModule({ declarations: [
        AppComponent,
        HeaderComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        BrowserModule,
        RouterModule,
        AdministrationModule,
        HomeModule,
        SettingsModule,
        ReportsModule,
        DashboardModule,
        SchedulingModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatIconModule,
        MatMenuModule,
        MatToolbarModule,
        MatDividerModule,
        MatButtonModule, 
        MatTooltipModule,
        FilterAutocompleteComponent,
        CommonModule,
        NgIdleModule.forRoot(),
        GlassPanelComponent,
        FormsModule,
        NgxChartsModule,
        NgxGanttModule,
        NgxMatTimepickerModule,
        ToastrModule.forRoot({
            closeButton: true,
            timeOut: 5000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true
        }),
        ], providers: [
        ...provideNucleusUiBase({
            applicationConfig: ApplicationConfigService,
            permissionChecker: PermissionCheckerService,
            uiConfiguration:   UIConfigurationService,
            fileViewer:        DefaultFileViewer,
        }),
        { provide: NUCLEUS_THEME_STORAGE_KEY, useValue: 'ts-theme' },
        // NUC-179: the single capability gate every nucleus-* lib injects.
        // PermissionsService.hasCapability(cap) satisfies NucleusCapabilityContract.
        { provide: CAPABILITY_CHECKER, useExisting: PermissionsService },
        provideAppInitializer(() => {
        const initializerFn = (appInitializer)(inject(AuthenticationService), inject(LoadingService), inject(InitService), inject(UserService), inject(Router));
        return initializerFn();        }),
        provideUserClient(),
        provideAuditClient(),
        provideMailClient(),
        importProvidersFrom(SchedulerApiModule),
        importProvidersFrom(UserDataApiModule),
        importProvidersFrom(AuthenticationApiModule),
        importProvidersFrom(RunnerApiModule),
        importProvidersFrom(CapabilityApiModule),
        provideOrgUnitClient(),
        provideSystemPropertyClient(),
        // Reference Data — embedded OpenAPI client mounts at the default
        // /api/reference-data-service basePath; the explicit BFF entry in
        // server/routes.js strips /api → /reference-data-service/v1/{bucket}.
        provideReferenceDataClient(),
        // NUC-179: connector lib's embedded OpenAPI client mounts at the default
        // /api/connectors-service basePath; the explicit BFF entry in
        // server/routes.js strips /api/connectors-service → /connectors-service/...
        provideConnectorClient(),
        // NUC-179: connector-admin-ui 0.0.3 moved the editor from a Material
        // dialog to sibling routes (connectors/new, connectors/:id), which no
        // longer inherit the list shell's component-level providers. Hoist the
        // notifier adapter to root so inject(CONNECTOR_NOTIFIER) resolves on the
        // routed edit page (otherwise NullInjectorError).
        { provide: CONNECTOR_NOTIFIER, useClass: TasksenseConnectorNotifierAdapter },
        { provide: HTTP_INTERCEPTORS, useClass: AppInterceptorService, multi: true },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic', appearance: 'outline'} },
        { provide: GANTT_GLOBAL_CONFIG, useValue: { locale: GanttI18nLocale.enUs} },
        { provide: SchedulerBASE_PATH, useValue: '/api/scheduler-service' },
        { provide: UserDataBASE_PATH, useValue: '/api/user-data-service' },
        { provide: AuthenticationBASE_PATH, useValue: '/api/authentication-service' },
        { provide: RunnerBASE_PATH, useValue: '/api/runner-service' },
        { provide: CapabilityBASE_PATH, useValue: '/api/capability-service' },
        OverlayService,
        LoadingService,
        UserService,
        AuditService,
        AuthenticationService,
        AppInterceptorService,
        CookieService,
        ApplicationConfigService,
        InitService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
